import React from 'react'
import { useSelector } from 'react-redux'
import { ChevronDown, ChevronLeft, ChevronRight, Store } from 'lucide-react'
import { Button, DropdownMenu } from 'components/ui'
import useLocaleText from 'components/useLocaleText'
import { isRtlSelector } from 'redux/selectors/general'
import { useDashboardReview } from '../../hooks/useDashboardReview'
import { useTaskOverviewContext } from '../../TaskOverviewStackProvider'
import { ExecutionStatus } from '../ExecutionStatus'
import { reviewLiterals } from './literals'
import styles from './styles.module.css'

export function ContextNavigation() {
  const review = useDashboardReview()!
  const { isSubtaskBusy } = useTaskOverviewContext()
  const t = useLocaleText(reviewLiterals)
  const rtl = useSelector(isRtlSelector)
  const position = review.contexts.findIndex((item) => item.contextId === review.task.dashboardContextId)
  const current = review.contexts[position]
  const busy = isSubtaskBusy || review.loading
  return (
    <div className={`${styles.navigation} ${styles.contextNavigation}`}>
      <DropdownMenu
        triggerVariant="ghost"
        triggerButtonProps={{
          disabled: busy,
          'aria-label': `${t.switchContext}: ${current?.name || ''}`,
          flex: 1,
          minW: 0,
          h: '30px',
          px: '6px',
          _hover: { bg: 'transparent', color: '#0F172A' },
          _active: { bg: 'transparent' },
        }}
        triggerContent={
          <>
            <Store size={15} />
            <span className={styles.contextName} title={current?.name}>
              {current?.name || t.contexts}
            </span>
            <ChevronDown size={14} />
          </>
        }
        menuWidth="min(340px, calc(100vw - 32px))"
        menuClassName={styles.navigationMenu}
        maxListHeight="min(420px, 65vh)"
        placement={rtl ? 'bottom-start' : 'bottom-end'}
        selectedId={current?.contextId}
        selectionIndicator="radio"
        isDisabled={busy}
        options={review.contexts.map((item, index) => ({
          disabled: busy,
          id: item.contextId,
          value: item.contextId,
          sectionTitle: index === 0 ? <strong className={styles.menuTitle}>{t.switchContext}</strong> : undefined,
          label: (
            <span className={styles.contextRow}>
              <span>{item.name}</span>
              <ExecutionStatus status={review.statusFor(item.contextId, review.selectedId)} />
            </span>
          ),
        }))}
        onSelect={(item) => review.switchContext(item.value)}
      />
      <div className={styles.pager}>
        <Button
          variant="icon"
          _hover={{ bg: 'transparent', color: '#0F172A' }}
          _active={{ bg: 'transparent' }}
          isDisabled={busy || position <= 0}
          aria-label={t.previousContext}
          onClick={() => review.switchContext(review.contexts[position - 1].contextId)}
        >
          {rtl ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
        </Button>
        <bdi>
          {position + 1} / {review.contexts.length}
        </bdi>
        <Button
          variant="icon"
          _hover={{ bg: 'transparent', color: '#0F172A' }}
          _active={{ bg: 'transparent' }}
          isDisabled={busy || position < 0 || position >= review.contexts.length - 1}
          aria-label={t.nextContext}
          onClick={() => review.switchContext(review.contexts[position + 1].contextId)}
        >
          {rtl ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
        </Button>
      </div>
    </div>
  )
}
